import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Eye,
  RefreshCw,
  Search,
  Shield,
  User,
  Users,
  AlertCircle,
  X,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Database,
  Briefcase,
  Home,
  CreditCard,
  Globe,
  ClipboardCheck,
  Send,
  HelpCircle,
  Download,
  Trash2,
  Upload,
  Paperclip,
} from 'lucide-react';
import {
  fetchUsers,
  fetchUserDetail,
  fetchFlowSessionDetail,
  fetchFlowSessionAeat,
  uploadUserDocument,
  listUserDocuments,
  downloadUserDocument,
  deleteUserDocument,
  type UserSummary,
  type UserDetail,
  type FlowSessionDetail,
  type FlowSessionSummary,
  type AEATFiscalSnapshot,
  type UserDocumentResponse,
} from '../features/admin/adminReviewApi';

import {
  buildSectionData,
  type QuestionMeta,
  type Section,
} from '../shared/surveyDataMapper';

import { clearAdminReviewToken, getAdminReviewToken } from '../shared/adminReviewAuth';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function sourceBadge(source: string | null | undefined) {
  if (!source) return null;
  const map: Record<string, { bg: string; text: string; label: string }> = {
    app:    { bg: 'bg-blue-50 border-blue-200',   text: 'text-blue-700',  label: 'App' },
    client: { bg: 'bg-teal-50 border-teal-200',   text: 'text-teal-700',  label: 'Client' },
    direct: { bg: 'bg-gray-50 border-gray-200',   text: 'text-gray-600',  label: 'Direct' },
  };
  const s = map[source];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function statusBadge(status: string) {
  const map: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    in_progress: {
      bg: 'bg-blue-50 border-blue-200',
      text: 'text-blue-700',
      icon: <Clock className="w-3 h-3" />,
    },
    completed: {
      bg: 'bg-green-50 border-green-200',
      text: 'text-green-700',
      icon: <CheckCircle className="w-3 h-3" />,
    },
    abandoned: {
      bg: 'bg-gray-50 border-gray-200',
      text: 'text-gray-600',
      icon: <XCircle className="w-3 h-3" />,
    },
  };
  const s = map[status] ?? map.in_progress;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${s.bg} ${s.text}`}>
      {s.icon}
      {status}
    </span>
  );
}

type View =
  | { kind: 'users' }
  | { kind: 'user'; userUuid: string }
  | { kind: 'session'; sessionUuid: string };

const AdminReviewPage: React.FC = () => {
  const [token, setToken] = useState<string>(() => getAdminReviewToken() ?? '');
  const [view, setView] = useState<View>({ kind: 'users' });

  useEffect(() => {
    const stored = getAdminReviewToken();
    if (!stored) {
      return;
    }
    fetchUsers(stored, { limit: 1 })
      .then(() => {
        setToken(stored);
        setView({ kind: 'users' });
      })
      .catch(() => {
        clearAdminReviewToken();
        window.location.reload();
      });
  }, []);

  const handleLogout = () => {
    clearAdminReviewToken();
    setToken('');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-primary-600" />
            <h1 className="text-lg font-semibold text-gray-900">Admin Review</h1>
            {view.kind !== 'users' && (
              <button
                onClick={() => setView({ kind: 'users' })}
                className="ml-2 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                All Users
              </button>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {view.kind === 'users' && <UserListView token={token} onSelectUser={(uuid) => setView({ kind: 'user', userUuid: uuid })} />}
        {view.kind === 'user' && (
          <UserDetailView
            token={token}
            userUuid={view.userUuid}
            onSelectSession={(uuid) => setView({ kind: 'session', sessionUuid: uuid })}
            onBack={() => setView({ kind: 'users' })}
          />
        )}
        {view.kind === 'session' && (
          <SessionDetailView
            token={token}
            sessionUuid={view.sessionUuid}
            onBack={() => setView({ kind: 'users' })}
            onSelectUser={(uuid) => setView({ kind: 'user', userUuid: uuid })}
          />
        )}
      </main>
    </div>
  );
};


/* ---------- User list view ---------- */

const UserListView: React.FC<{ token: string; onSelectUser: (uuid: string) => void }> = ({
  token,
  onSelectUser,
}) => {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 25;

  const loadUsers = useCallback(
    async (q: string, status: string, source: string, offset: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchUsers(token, {
          q: q || undefined,
          status: status || undefined,
          source: source || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        setUsers(res.items);
        setHasMore(res.has_more);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    loadUsers(query, statusFilter, sourceFilter, page * PAGE_SIZE);
  }, [loadUsers, query, statusFilter, sourceFilter, page]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-gray-700" />
          <h2 className="text-xl font-bold text-gray-900">Users</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, email, NIF..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0); }}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All statuses</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="abandoned">Abandoned</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All sources</option>
            <option value="app">App (innotaxes.com)</option>
            <option value="client">Client (/client)</option>
            <option value="direct">Direct (/new)</option>
          </select>
          <button
            onClick={() => loadUsers(query, statusFilter, sourceFilter, page * PAGE_SIZE)}
            disabled={loading}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading && users.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No users found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NIF/NIE</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.user_uuid} className="hover:bg-gray-50 cursor-pointer" onClick={() => onSelectUser(u.user_uuid)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{u.full_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{u.nif}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.country ?? '\u2014'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <span>Page {page + 1}</span>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ---------- Documents section ---------- */

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* Read-only document list for the user overview — shows all user docs with declaration context. */
const DocumentsSection: React.FC<{ token: string; userUuid: string }> = ({ token, userUuid }) => {
  const [docs, setDocs] = useState<UserDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDocs(await listUserDocuments(userUuid, token));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, userUuid]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (doc: UserDocumentResponse) => {
    setActionError(null);
    try {
      const blob = await downloadUserDocument(doc.id, token);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const handleDelete = async (docId: string) => {
    setActionError(null);
    try {
      await deleteUserDocument(docId, token);
      setConfirmDelete(null);
      await load();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
      <div className="px-5 py-3.5 border-b border-gray-200 flex items-center gap-2">
        <Paperclip className="w-4 h-4 text-gray-600" />
        <h3 className="font-semibold text-gray-900">All Documents</h3>
        {!loading && (
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {docs.length}
          </span>
        )}
        <span className="ml-auto text-xs text-gray-400">Upload documents inside a specific session below</span>
      </div>

      {actionError && (
        <div className="px-5 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {actionError}
        </div>
      )}

      {error && (
        <div className="px-5 py-3 text-sm text-red-600 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="px-5 py-6 text-center text-sm text-gray-400">Loading...</div>
      ) : docs.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">No documents uploaded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Declaration</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {docs.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm text-gray-900 font-medium truncate max-w-xs" title={doc.filename}>
                    {doc.filename}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{doc.label ?? <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 tabular-nums">{formatFileSize(doc.size_bytes)}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-500">{formatDate(doc.created_at)}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-gray-400 truncate max-w-[160px]" title={doc.flow_session_uuid ?? undefined}>
                    {doc.flow_session_uuid ?? <span className="text-gray-300">unlinked</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {confirmDelete === doc.id ? (
                      <span className="inline-flex items-center gap-2 text-xs">
                        <span className="text-gray-600">Delete?</span>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="text-red-600 hover:text-red-700 font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <button
                          onClick={() => handleDownload(doc)}
                          title="Download"
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(doc.id)}
                          title="Delete"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* Session-scoped document upload — documents uploaded here are bound to the specific declaration. */
const SessionDocumentsSection: React.FC<{ token: string; userUuid: string; sessionUuid: string }> = ({
  token,
  userUuid,
  sessionUuid,
}) => {
  const [docs, setDocs] = useState<UserDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listUserDocuments(userUuid, token);
      setDocs(all.filter((d) => d.flow_session_uuid === sessionUuid));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, userUuid, sessionUuid]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError(null);
    try {
      await uploadUserDocument(userUuid, uploadFile, uploadLabel, token, sessionUuid);
      setShowUpload(false);
      setUploadFile(null);
      setUploadLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e: unknown) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: UserDocumentResponse) => {
    setActionError(null);
    try {
      const blob = await downloadUserDocument(doc.id, token);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const handleDelete = async (docId: string) => {
    setActionError(null);
    try {
      await deleteUserDocument(docId, token);
      setConfirmDelete(null);
      await load();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const cancelUpload = () => {
    setShowUpload(false);
    setUploadFile(null);
    setUploadLabel('');
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
      <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Declaration Documents</h3>
          {!loading && (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {docs.length}
            </span>
          )}
        </div>
        {!showUpload && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Upload File
          </button>
        )}
      </div>

      {showUpload && (
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:border file:border-gray-300 file:rounded-md file:text-sm file:bg-white file:text-gray-700 hover:file:bg-gray-50"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Label (optional)</label>
              <input
                type="text"
                placeholder='e.g. "Tax return 2024"'
                value={uploadLabel}
                onChange={(e) => setUploadLabel(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 rounded-lg transition-colors"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={cancelUpload}
                disabled={uploading}
                className="px-4 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
          {uploadError && (
            <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {uploadError}
            </p>
          )}
        </div>
      )}

      {actionError && (
        <div className="px-5 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {actionError}
        </div>
      )}

      {error && (
        <div className="px-5 py-3 text-sm text-red-600 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="px-5 py-6 text-center text-sm text-gray-400">Loading...</div>
      ) : docs.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">No documents for this declaration yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {docs.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm text-gray-900 font-medium truncate max-w-xs" title={doc.filename}>
                    {doc.filename}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{doc.label ?? <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 tabular-nums">{formatFileSize(doc.size_bytes)}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-500">{formatDate(doc.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {confirmDelete === doc.id ? (
                      <span className="inline-flex items-center gap-2 text-xs">
                        <span className="text-gray-600">Delete?</span>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="text-red-600 hover:text-red-700 font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <button
                          onClick={() => handleDownload(doc)}
                          title="Download"
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(doc.id)}
                          title="Delete"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ---------- User detail view ---------- */

const UserDetailView: React.FC<{
  token: string;
  userUuid: string;
  onSelectSession: (uuid: string) => void;
  onBack: () => void;
}> = ({ token, userUuid, onSelectSession, onBack }) => {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchUserDetail(token, userUuid)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [token, userUuid]);

  if (loading) return <div className="p-12 text-center text-gray-500">Loading user...</div>;
  if (error) return <ErrorBanner message={error} />;
  if (!detail) return <ErrorBanner message="User not found" />;

  return (
    <div>
      <button onClick={onBack} className="mb-4 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to Users
      </button>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
            <User className="w-7 h-7 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900">{detail.full_name}</h2>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Email</span>
                <p className="font-medium text-gray-900">{detail.email}</p>
              </div>
              <div>
                <span className="text-gray-500">NIF/NIE</span>
                <p className="font-mono font-medium text-gray-900">{detail.nif}</p>
              </div>
              <div>
                <span className="text-gray-500">Country</span>
                <p className="font-medium text-gray-900">{detail.country ?? '\u2014'}</p>
              </div>
              <div>
                <span className="text-gray-500">Created</span>
                <p className="text-gray-700">{formatDate(detail.created_at)}</p>
              </div>
              <div>
                <span className="text-gray-500">Updated</span>
                <p className="text-gray-700">{formatDate(detail.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <FileText className="w-5 h-5 text-gray-600" />
        Flow Sessions ({detail.flow_sessions.length})
      </h3>

      {detail.flow_sessions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No flow sessions yet.
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {detail.flow_sessions.map((s) => (
            <SessionCard key={s.session_uuid} session={s} onClick={() => onSelectSession(s.session_uuid)} />
          ))}
        </div>
      )}

      <DocumentsSection token={token} userUuid={userUuid} />
    </div>
  );
};

/* ---------- Session detail view ---------- */

const SessionDetailView: React.FC<{
  token: string;
  sessionUuid: string;
  onBack: () => void;
  onSelectUser: (uuid: string) => void;
}> = ({ token, sessionUuid, onBack, onSelectUser }) => {
  const [detail, setDetail] = useState<FlowSessionDetail | null>(null);
  const [aeatSnapshots, setAeatSnapshots] = useState<AEATFiscalSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSnapshot, setExpandedSnapshot] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchFlowSessionDetail(token, sessionUuid),
      fetchFlowSessionAeat(token, sessionUuid).catch(() => [] as AEATFiscalSnapshot[]),
    ])
      .then(([sess, snaps]) => {
        setDetail(sess);
        setAeatSnapshots(snaps);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [token, sessionUuid]);

  const screenTitleMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (!detail?.question_metadata) return map;
    for (const qm of Object.values(detail.question_metadata)) {
      if (qm.screen_id && qm.screen_title && !map[qm.screen_id]) {
        map[qm.screen_id] = qm.screen_title;
      }
    }
    return map;
  }, [detail]);

  const sectionData = useMemo((): Section[] => {
    if (!detail?.answers) return [];
    return buildSectionData(detail.answers, detail.question_metadata ?? {});
  }, [detail]);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [expandedScreens, setExpandedScreens] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (sectionData.length > 0) {
      setExpandedSections(new Set(sectionData.map((s) => s.id)));
      const allScreenIds = new Set<string>();
      for (const sec of sectionData) {
        for (const scr of sec.screens) allScreenIds.add(scr.screenId);
      }
      setExpandedScreens(allScreenIds);
    }
  }, [sectionData]);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleScreen = (id: string) => {
    setExpandedScreens((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) return <div className="p-12 text-center text-gray-500">Loading session...</div>;
  if (error) return <ErrorBanner message={error} />;
  if (!detail) return <ErrorBanner message="Session not found" />;

  return (
    <div>
      <button onClick={onBack} className="mb-4 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to Users
      </button>

      {/* Session header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-bold text-gray-900">Flow Session</h2>
              {statusBadge(detail.status)}
            </div>
            <p className="text-xs text-gray-400 font-mono mt-1">{detail.session_uuid}</p>
          </div>
          {detail.user && (
            <button
              onClick={() => onSelectUser(detail.user!.user_uuid)}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <User className="w-3.5 h-3.5" />
              {detail.user.full_name}
            </button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <InfoCell label="Country" value={detail.country ?? '\u2014'} />
          <InfoCell label="Tax Year" value={String(detail.tax_year)} />
          <InfoCell label="Language" value={detail.language} />
          <InfoCell
            label="Current Screen"
            value={
              detail.current_screen_id
                ? screenTitleMap[detail.current_screen_id] || detail.current_screen_id
                : '\u2014'
            }
          />
          <InfoCell label="Source" value={detail.source ?? '—'} />
          <InfoCell label="Created" value={formatDate(detail.created_at)} />
          <InfoCell label="Updated" value={formatDate(detail.updated_at)} />
          <InfoCell label="Completed" value={formatDate(detail.completed_at)} />
          <InfoCell label="Screens Visited" value={String(detail.visited_screens.length)} />
        </div>
      </div>

      {/* Answers — sidebar-style sections */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-gray-900">
              Questionnaire Answers
            </h3>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {Object.keys(detail.answers).length} answers
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                const allIds = sectionData.map((s) => s.id);
                const allExpanded = allIds.every((id) => expandedSections.has(id));
                if (allExpanded) {
                  setExpandedSections(new Set());
                  setExpandedScreens(new Set());
                } else {
                  setExpandedSections(new Set(allIds));
                  const screenIds = new Set<string>();
                  for (const sec of sectionData) for (const scr of sec.screens) screenIds.add(scr.screenId);
                  setExpandedScreens(screenIds);
                }
              }}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-50"
            >
              {sectionData.every((s) => expandedSections.has(s.id)) ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
        </div>

        {sectionData.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No answers recorded.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sectionData.map((section) => {
              const sectionOpen = expandedSections.has(section.id);
              const totalAnswers = section.screens.reduce((sum, s) => sum + s.items.length, 0);
              const SectionIcon = SECTION_ICON_MAP[section.id] || HelpCircle;
              return (
                <div key={section.id}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                      sectionOpen ? 'bg-teal-50/60' : 'hover:bg-gray-50'
                    }`}
                  >
                    <SectionIcon className={`w-5 h-5 shrink-0 ${sectionOpen ? 'text-teal-600' : 'text-gray-400'}`} />
                    <span className={`flex-1 text-sm font-semibold ${sectionOpen ? 'text-teal-800' : 'text-gray-700'}`}>
                      {section.label}
                    </span>
                    <span className="text-xs text-gray-400 mr-1">
                      {section.screens.length} {section.screens.length === 1 ? 'screen' : 'screens'} &middot; {totalAnswers} {totalAnswers === 1 ? 'answer' : 'answers'}
                    </span>
                    {sectionOpen ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {sectionOpen && (
                    <div className="pl-5 pr-3 pb-2">
                      {section.screens.map((screen) => {
                        const screenOpen = expandedScreens.has(screen.screenId);
                        return (
                          <div key={screen.screenId} className="ml-3 border-l-2 border-gray-200">
                            <button
                              onClick={() => toggleScreen(screen.screenId)}
                              className={`w-full flex items-center gap-2 pl-4 pr-3 py-2 text-left transition-colors rounded-r-lg ${
                                screenOpen ? 'bg-gray-50 border-l-2 -ml-[2px] border-teal-400' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm truncate ${screenOpen ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                                  {cleanScreenTitle(screen.screenTitle) || screen.screenId}
                                </p>
                                {screen.screenTitle && (
                                  <p className="text-[10px] font-mono text-gray-400 truncate">{screen.screenId}</p>
                                )}
                              </div>
                              <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">
                                {screen.items.length}
                              </span>
                              {screenOpen ? (
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              )}
                            </button>

                            {screenOpen && (
                              <div className="ml-4 mr-1 mb-2 mt-1 rounded-lg border border-gray-200 bg-white overflow-hidden">
                                {screen.items.map(({ key, value, meta }, idx) => (
                                  <div
                                    key={key}
                                    className={`flex gap-4 px-4 py-2.5 ${idx > 0 ? 'border-t border-gray-100' : ''} hover:bg-gray-50/50`}
                                  >
                                    <div className="w-2/5 min-w-0">
                                      {meta?.question_text ? (
                                        <>
                                          <p className="text-sm text-gray-800">{meta.question_text}</p>
                                          <p className="text-[10px] font-mono text-gray-400 mt-0.5 truncate">{key}</p>
                                        </>
                                      ) : (
                                        <p className="text-sm font-mono text-gray-600">{key}</p>
                                      )}
                                    </div>
                                    <div className="flex-1 text-sm text-gray-900 min-w-0">
                                      <AnswerValue value={value} fieldType={meta?.field_type} questionKey={key} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Screen history */}
      {detail.screen_history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Screen History ({detail.screen_history.length})</h3>
          </div>
          <div className="p-4 flex flex-wrap gap-1.5">
            {detail.screen_history.map((sid, i) => {
              const screenTitle = screenTitleMap[sid];
              return (
                <span
                  key={`${sid}-${i}`}
                  className={`px-2 py-1 text-xs rounded-md ${
                    sid === detail.current_screen_id
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                  title={sid}
                >
                  {i + 1}. {screenTitle || sid}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Tax calculation */}
      {detail.last_tax_calculation && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Tax Calculation Result</h3>
          </div>
          <div className="p-4">
            <pre className="text-xs text-gray-800 bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-auto max-h-96 whitespace-pre-wrap font-mono">
              {JSON.stringify(detail.last_tax_calculation, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* AEAT snapshots */}
      {aeatSnapshots.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Database className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-gray-900">AEAT Fiscal Data ({aeatSnapshots.length})</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {aeatSnapshots.map((snap) => (
              <div key={snap.snapshot_uuid} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">Tax Year: {snap.tax_year ?? '\u2014'}</span>
                    <span className="ml-3 text-gray-500">NIF: <span className="font-mono">{snap.nif ?? '\u2014'}</span></span>
                    <span className="ml-3 text-gray-400">{formatDate(snap.fetched_at)}</span>
                  </div>
                  <button
                    onClick={() => setExpandedSnapshot(expandedSnapshot === snap.snapshot_uuid ? null : snap.snapshot_uuid)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md"
                    title="View fiscal data"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                {expandedSnapshot === snap.snapshot_uuid && (
                  <pre className="mt-3 text-xs text-gray-800 bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                    {JSON.stringify(snap.parsed_fiscal_data, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.user && (
        <SessionDocumentsSection
          token={token}
          userUuid={detail.user.user_uuid}
          sessionUuid={detail.session_uuid}
        />
      )}
    </div>
  );
};

/* ---------- Small shared components ---------- */

const SessionCard: React.FC<{ session: FlowSessionSummary; onClick: () => void }> = ({ session, onClick }) => (
  <button
    onClick={onClick}
    className="w-full bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:border-primary-300 hover:shadow-md transition-all text-left"
  >
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {statusBadge(session.status)}
          {sourceBadge(session.source)}
          <span className="text-sm text-gray-500">
            {session.country ?? 'Unknown'} &middot; {session.tax_year}
          </span>
        </div>
        <p className="text-xs font-mono text-gray-400 truncate">{session.session_uuid}</p>
        <div className="mt-1 flex gap-4 text-xs text-gray-500">
          <span>Screen: <span className="font-mono">{session.current_screen_id ?? '\u2014'}</span></span>
          <span>Started: {formatDate(session.created_at)}</span>
          {session.completed_at && <span>Completed: {formatDate(session.completed_at)}</span>}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
    </div>
  </button>
);

const InfoCell: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <span className="text-gray-500 text-xs">{label}</span>
    <p className={`text-gray-900 font-medium ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</p>
  </div>
);

const SECTION_ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  start: Home,
  'my-information': User,
  income: Briefcase,
  deductions: CreditCard,
  'foreign-reporting': Globe,
  summary: ClipboardCheck,
  submission: Send,
  other: HelpCircle,
};

function cleanScreenTitle(title: string): string {
  if (!title) return '';
  return title
    .replace(/^(Welcome & Setup|Consent & Access|Getting Started)\s*/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatCurrency(val: unknown): string | null {
  const n = typeof val === 'string' ? parseFloat(val) : typeof val === 'number' ? val : NaN;
  if (isNaN(n)) return null;
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isCurrencyField(_key: string, fieldType?: string): boolean {
  return fieldType === 'currency' || fieldType === 'tax_total';
}

const AnswerValue: React.FC<{ value: unknown; fieldType?: string; questionKey?: string }> = ({ value, fieldType, questionKey }) => {
  if (value === null || value === undefined) return <span className="text-gray-400 italic">null</span>;
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
        {value ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        {String(value)}
      </span>
    );
  }
  const isCurrency = isCurrencyField(questionKey ?? '', fieldType);
  if (typeof value === 'number') {
    if (isCurrency) {
      return <span className="text-emerald-700 font-mono font-medium">{'\u20AC'}{formatCurrency(value)}</span>;
    }
    return <span className="text-blue-700 font-mono">{value}</span>;
  }
  if (typeof value === 'string') {
    if (isCurrency) {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return <span className="text-emerald-700 font-mono font-medium">{'\u20AC'}{formatCurrency(parsed)}</span>;
      }
    }
    if (value.length > 200) {
      return <span className="break-all">{value.slice(0, 200)}<span className="text-gray-400">... ({value.length} chars)</span></span>;
    }
    return <span className="break-all">{value}</span>;
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value, null, 2);
    return (
      <pre className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-100 overflow-auto max-h-40 whitespace-pre-wrap font-mono">
        {json}
      </pre>
    );
  }
  return <span>{String(value)}</span>;
};

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
    <AlertCircle className="w-4 h-4 flex-shrink-0" />
    {message}
  </div>
);

export default AdminReviewPage;
